import {
  Button,
  FullscreenModal,
  FullscreenModalContent,
  FullscreenModalTitle,
  Input,
  RadioGroup,
  RadioGroupCardItem,
  useRadioGroupContainerStyles,
  PhoneInput,
  useFormValidation,
} from "@porchpass/ui";
import { useEffect, useState } from "react";
import { z } from "zod";
import type { EditEmployeeFormData } from "src/types/employee";

// Zod schema for form validation
const createEmployeeSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  phone_number: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  role: z.enum(["SALES_REPRESENTATIVE", "GENERAL_MANAGER"], {
    errorMap: () => ({ message: "Role is required" }),
  }),
});

// Form state type that allows empty role (validated on submit)
type AddEmployeeFormState = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  title: string;
  role: "SALES_REPRESENTATIVE" | "GENERAL_MANAGER" | "";
} & Record<string, unknown>;

const INITIAL_FORM_STATE: AddEmployeeFormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  title: "",
  role: "",
};

const SalesRepresentativeIcon = () => (
  <div className="flex relative justify-center items-center w-8 h-8">
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0"
      preserveAspectRatio="none"
      style={{ overflow: "visible", display: "block" }}
    >
      <path
        id="star-1"
        d="M9.73913 4.35907C12.0087 -1.45302 19.9913 -1.45302 22.2609 4.35907C22.8338 5.8262 24.1735 6.82697 25.7038 6.93368C31.7564 7.35573 34.2371 15.1763 29.5766 19.1807C28.397 20.1943 27.8856 21.8159 28.2612 23.3511C29.745 29.4171 23.2731 34.2366 18.131 30.9167C16.8303 30.077 15.1697 30.077 13.869 30.9167C8.72694 34.2366 2.25495 29.4171 3.73885 23.3511C4.11441 21.8159 3.60304 20.1943 2.42344 19.1807C-2.23709 15.1763 0.243611 7.35573 6.29618 6.93368C7.82648 6.82697 9.16623 5.8262 9.73913 4.35907Z"
        fill="white"
      />
    </svg>
    <svg
      width="18"
      height="18"
      viewBox="0 0 16.125 16.125"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="relative z-10"
    >
      <g>
        <path
          opacity="0.4"
          d="M9.26172 10.125C10.8092 10.125 12.2381 10.9542 13.0059 12.2979L13.7705 13.7568C12.311 15.2196 10.2921 16.125 8.0625 16.125C5.83301 16.1249 3.81491 15.2195 2.35547 13.7568L3.12012 12.2979C3.88795 10.9543 5.31676 10.125 6.86426 10.125H9.26172ZM8.0625 3.5625C9.51225 3.5625 10.6875 4.73775 10.6875 6.1875C10.6875 7.63725 9.51225 8.8125 8.0625 8.8125C6.61275 8.8125 5.4375 7.63725 5.4375 6.1875C5.4375 4.73775 6.61275 3.5625 8.0625 3.5625Z"
          fill="#226392"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.0625 1.46591C4.4193 1.46591 1.46591 4.4193 1.46591 8.0625C1.46591 11.7057 4.4193 14.6591 8.0625 14.6591C11.7057 14.6591 14.6591 11.7057 14.6591 8.0625C14.6591 4.4193 11.7057 1.46591 8.0625 1.46591ZM0 8.0625C0 3.6097 3.6097 0 8.0625 0C12.5153 0 16.125 3.6097 16.125 8.0625C16.125 12.5153 12.5153 16.125 8.0625 16.125C3.6097 16.125 0 12.5153 0 8.0625Z"
          fill="#226392"
        />
      </g>
    </svg>
  </div>
);

const GeneralManagerIcon = () => {
  const clipId = `general-manager-clip-${Math.random().toString(36).substr(2, 9)}`;
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 15.75 16.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[18px] h-[18px]"
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          opacity="0.4"
          d="M6.78269 0.197155C6.78269 0.197155 8.78033 0.175549 8.96738 0.19698C9.1758 0.220861 9.39503 0.276089 9.59955 0.424024C9.804 0.571892 9.92535 0.762885 10.0137 0.953445C10.093 1.1246 10.4177 2.09732 10.4929 2.32351C10.5983 2.62135 10.985 3.16742 11.6885 2.96897C11.9345 2.89903 13.0912 2.57216 13.2892 2.54066C13.5096 2.50563 13.7507 2.50116 13.9999 2.60268C14.249 2.70416 14.4185 2.87579 14.552 3.05477L15.6244 4.91441C15.7084 5.10857 15.7704 5.32778 15.7439 5.58058C15.7173 5.83341 15.6112 6.03493 15.4888 6.20736C15.4122 6.31523 14.9392 6.85101 14.6051 7.22647C14.4596 7.38994 14.3405 7.52303 14.2919 7.57718C14.1033 7.81455 13.8393 8.41605 14.2918 8.92275C14.4522 9.10125 15.3788 10.1377 15.4888 10.2926C15.6112 10.465 15.7173 10.6665 15.7439 10.9193C15.7704 11.1722 15.7084 11.3913 15.6244 11.5855C15.6244 11.5855 14.6721 13.2841 14.552 13.4451C14.4185 13.6241 14.249 13.7957 13.9998 13.8972C13.7506 13.9988 13.5096 13.9943 13.2892 13.9592C13.0912 13.9277 11.9345 13.6009 11.6885 13.5309C10.9472 13.3217 10.5825 13.874 10.4929 14.1764C10.4178 14.4026 10.093 15.3754 10.0137 15.5465C9.92535 15.7371 9.804 15.9281 9.59955 16.076C9.39503 16.2239 9.1758 16.2791 8.96738 16.303C8.96738 16.303 6.96977 16.3244 6.78269 16.303C6.57424 16.2791 6.35503 16.2239 6.15048 16.076C5.94602 15.9281 5.82468 15.7371 5.73633 15.5465C5.65698 15.3754 5.33228 14.4026 5.25717 14.1764C5.00724 13.4291 4.35599 13.4347 4.0616 13.5309C4.0616 13.5309 2.6589 13.9277 2.4608 13.9592C2.24048 13.9943 1.99946 13.9988 1.75022 13.8972C1.50107 13.7957 1.33153 13.6241 1.19806 13.4451C1.07796 13.2841 0.125576 11.5855 0.125576 11.5855C0.0416231 11.3913 -0.0203104 11.1722 0.0062093 10.9193C0.0327314 10.6665 0.138814 10.465 0.261262 10.2926C0.371212 10.1377 1.45818 8.92275 1.45818 8.92275C1.93511 8.38875 1.65689 7.80315 1.45816 7.57718C1.45816 7.57718 0.371209 6.36219 0.261259 6.20736C0.138811 6.03493 0.0327299 5.83341 0.00620787 5.58059C-0.02031 5.32778 0.0416208 5.10857 0.125574 4.91441C0.125574 4.91441 1.07797 3.2158 1.19806 3.05477C1.33154 2.87579 1.50107 2.70416 1.75022 2.60268C1.99945 2.50116 2.24047 2.50563 2.46078 2.54066C2.65888 2.57216 4.06163 2.96897 4.06163 2.96897C4.87815 3.19947 5.19889 2.63471 5.25719 2.32351C5.33231 2.09732 5.65698 1.1246 5.73633 0.953445C5.82468 0.762885 5.94603 0.571892 6.15048 0.424024C6.35503 0.276089 6.57424 0.221035 6.78269 0.197155Z"
          fill="#6F41E6"
        />
        <path
          d="M7.8754 9.16992C9.20576 9.17004 10.3664 9.89359 10.9867 10.9658C11.1423 11.2348 11.0505 11.5788 10.7816 11.7344C10.5127 11.8899 10.1686 11.7982 10.0131 11.5293C9.58557 10.7903 8.78808 10.295 7.8754 10.2949C6.96265 10.2949 6.16429 10.7902 5.73673 11.5293C5.58113 11.7981 5.23704 11.8899 4.96817 11.7344C4.69947 11.5788 4.60759 11.2347 4.76309 10.9658C5.38349 9.89342 6.54485 9.16992 7.8754 9.16992ZM7.8754 4.3125C9.01426 4.3127 9.9379 5.23604 9.9379 6.375C9.9379 7.51398 9.01426 8.4373 7.8754 8.4375C6.73628 8.4375 5.8129 7.5141 5.8129 6.375C5.8129 5.23592 6.73628 4.3125 7.8754 4.3125Z"
          fill="#6F41E6"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="15.75" height="16.5" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const AddEmployeeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.33333 12.25V11.0833C9.33333 10.4645 9.08735 9.87097 8.64926 9.43213C8.21117 8.99328 7.61812 8.74667 7 8.74667H3.5C2.88189 8.74667 2.28883 8.99328 1.85074 9.43213C1.41265 9.87097 1.16667 10.4645 1.16667 11.0833V12.25M11.6667 4.66667V8.16667M13.4167 6.41667H9.91667M7.29167 4.08333C7.29167 5.37199 6.24699 6.41667 4.95833 6.41667C3.66968 6.41667 2.625 5.37199 2.625 4.08333C2.625 2.79468 3.66968 1.75 4.95833 1.75C6.24699 1.75 7.29167 2.79468 7.29167 4.08333Z"
      stroke="#1453F5"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Permissions Section Component with synced container border radius
function PermissionsSection({
  selectedRole,
  onRoleChange,
  error,
}: {
  selectedRole: string;
  onRoleChange: (role: string) => void;
  error?: string;
}) {
  const containerStyles = useRadioGroupContainerStyles({
    selectedValue: selectedRole,
    firstValue: "SALES_REPRESENTATIVE",
    lastValue: "GENERAL_MANAGER",
    adjacentToBottom: true,
  });

  return (
    <div className="mt-5">
      <div
        className={`bg-[#f3f5f8] pb-[6px] px-[6px] transition-shadow duration-200 ${error ? "border-[3px] border-[#872525] shadow-[0_0_0_4px_rgba(135,37,37,0.16)]" : " border-[1px] border-[#e4e7ec]"}`}
        style={containerStyles.style}
      >
        {/* Section Header */}
        <div className="px-4 pt-[22px] pb-4">
          <h3 className="text-[14px] font-medium text-[#111827] leading-none">
            Permissions
          </h3>
          <p className="text-[12px] font-normal text-[#4a5466] leading-none mt-3">
            Specify the role this user holds at the dealership.
          </p>
        </div>
        {/* Role Options */}
        <RadioGroup value={selectedRole} onValueChange={onRoleChange}>
          <RadioGroupCardItem
            value="SALES_REPRESENTATIVE"
            icon={<SalesRepresentativeIcon />}
            iconBgColor="bg-transparent"
            label="Sales Representative"
            description="Access to assigned deals only."
          />
          <RadioGroupCardItem
            value="GENERAL_MANAGER"
            icon={<GeneralManagerIcon />}
            iconBgColor="bg-[#f1edfb]"
            label="General Manager"
            description="Full access to all deals, data, dealer settings, and management."
          />
        </RadioGroup>
      </div>
      {error && (
        <p className="mt-2 text-[12px] font-normal text-[#872525] leading-4">
          {error}
        </p>
      )}
    </div>
  );
}

interface AddEmployeeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: EditEmployeeFormData) => Promise<void>;
  isSaving: boolean;
}

export default function AddEmployeeModal({
  open,
  onOpenChange,
  onSave,
  isSaving,
}: AddEmployeeModalProps) {
  const [formData, setFormData] =
    useState<AddEmployeeFormState>(INITIAL_FORM_STATE);

  // Form validation using Zod schema
  const { validate, fieldErrors, clearFieldError, clearAllErrors } =
    useFormValidation(createEmployeeSchema, {
      validationErrorToast: "Please fix the errors in the form",
    });

  const handleFieldChange = (
    field: keyof AddEmployeeFormState,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    clearFieldError(field);
  };

  const handleSave = async () => {
    const result = validate(formData);
    if (!result.success) {
      return;
    }

    await onSave(result.data);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    clearAllErrors();
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, clearAllErrors]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const headerContent = (
    <>
      <div className="w-[13.5px] h-[13.5px] flex items-center justify-center">
        <svg
          preserveAspectRatio="none"
          width="100%"
          height="100%"
          overflow="visible"
          style={{ display: "block" }}
          viewBox="0 0 16.1279 16.125"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="elements">
            <path
              id="Icon"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.7529 9.375C13.1654 9.375 13.5029 9.7125 13.5029 10.125V12H15.3779C15.7904 12 16.1279 12.3375 16.1279 12.75C16.1279 13.1625 15.7904 13.5 15.3779 13.5H13.5029V15.375C13.5029 15.7875 13.1654 16.125 12.7529 16.125C12.3404 16.125 12.0029 15.7875 12.0029 15.375V13.5H10.1279C9.71543 13.5 9.37793 13.1625 9.37793 12.75C9.37793 12.3375 9.71543 12 10.1279 12H12.0029V10.125C12.0029 9.7125 12.3404 9.375 12.7529 9.375Z"
              fill="#4075FF"
            />
            <path
              id="Tone"
              opacity="0.4"
              d="M9.91123 10.89L8.62872 10.5V9.04504C9.96372 8.00254 10.8787 6.75003 10.8787 4.06503C10.8787 2.32503 10.5637 0.75 8.65872 0.75C8.65872 0.75 7.99123 0 6.37123 0C4.09123 0 2.62872 1.37253 2.62872 4.06503C2.62872 6.75753 3.54372 8.00254 4.87872 9.04504V10.5L2.08872 11.34C1.04622 11.655 0.273725 12.495 0.0187248 13.5075C-0.0862752 13.9125 0.266217 14.25 0.678717 14.25H8.99623C8.53873 13.905 8.24623 13.365 8.24623 12.75C8.24623 11.79 8.97372 10.995 9.90372 10.89H9.91123Z"
              fill="#4075FF"
            />
          </g>
        </svg>
      </div>
      <FullscreenModalTitle>Add employee</FullscreenModalTitle>
    </>
  );

  const footerContent = (
    <>
      <Button variant="ghost" size="s" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="s"
        onClick={() => void handleSave()}
        disabled={isSaving}
        loading={isSaving}
      >
        <svg
          preserveAspectRatio="none"
          width="18px"
          height="18px"
          overflow="visible"
          style={{ display: "block" }}
          viewBox="0 0 16.125 16.125"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="elements">
            <path
              id="Tone"
              opacity="0.4"
              d="M8.0625 16.125C3.60975 16.125 0 12.5153 0 8.0625C0 3.60975 3.60975 0 8.0625 0C12.5153 0 16.125 3.60975 16.125 8.0625C16.125 12.5153 12.5153 16.125 8.0625 16.125Z"
              fill="#111827"
            />
            <path
              id="Icon"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.6151 6.31943C11.8948 6.01418 11.8746 5.53944 11.5693 5.25969C11.2641 4.97994 10.7893 5.00021 10.5096 5.30546L6.91407 9.22795L5.59258 7.90717C5.30008 7.61392 4.82457 7.61392 4.53207 7.90717C4.23882 8.19967 4.23882 8.67522 4.53207 8.96772L6.40707 10.8427C6.55182 10.9875 6.74907 11.0669 6.95382 11.0624C7.15782 11.0579 7.35208 10.9702 7.49008 10.8194L11.6151 6.31943Z"
              fill="white"
            />
          </g>
        </svg>
        Add employee
      </Button>
    </>
  );

  return (
    <FullscreenModal open={open} onOpenChange={handleOpenChange}>
      <FullscreenModalContent
        headerContent={headerContent}
        footerContent={footerContent}
        onEscapeKeyDown={handleClose}
        onInteractOutside={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        {/* Centered Content */}
        <div className="flex flex-col flex-1 justify-center items-center">
          <div className="flex flex-col items-center px-4 py-10 w-[1440px]">
            {/* Page Title */}
            <div className="flex gap-1 items-center mb-12">
              <h1 className="text-[28px] font-semibold text-[#111827] leading-8">
                Add
              </h1>
              <div className="bg-[#e5ebfd] rounded-[24px] px-[6px] py-1 flex items-center">
                <AddEmployeeIcon />
              </div>
              <h1 className="text-[28px] font-semibold text-[#111827] leading-8">
                a new employee
              </h1>
            </div>

            {/* Form Fields */}
            <div className="flex flex-col gap-1 w-full max-w-[628px]">
              {/* First name / Last name row */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <Input
                    label="First name"
                    value={formData.first_name}
                    onChange={(e) =>
                      handleFieldChange("first_name", e.target.value)
                    }
                    error={!!fieldErrors.first_name}
                    helperText={fieldErrors.first_name}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Last name"
                    value={formData.last_name}
                    onChange={(e) =>
                      handleFieldChange("last_name", e.target.value)
                    }
                    error={!!fieldErrors.last_name}
                    helperText={fieldErrors.last_name}
                  />
                </div>
              </div>

              {/* Email / Phone row */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <Input
                    label="Email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    type="email"
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                  />
                </div>
                <div className="w-[193px] flex-shrink-0">
                  <PhoneInput
                    label="Phone"
                    value={formData.phone_number}
                    onChange={(e) => handleFieldChange("phone_number", e)}
                    showOptional
                    error={!!fieldErrors.phone_number}
                    helperText={fieldErrors.phone_number}
                  />
                </div>
              </div>

              {/* Title field */}
              <Input
                label="Title"
                value={formData.title}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                error={!!fieldErrors.title}
                helperText={fieldErrors.title}
              />

              {/* Permissions Section */}
              <PermissionsSection
                selectedRole={formData.role}
                onRoleChange={(role) => handleFieldChange("role", role)}
                error={fieldErrors.role}
              />
            </div>
          </div>
        </div>
      </FullscreenModalContent>
    </FullscreenModal>
  );
}
